'use client';

import { useParams } from 'next/navigation';
import { type FC, memo, useEffect, useMemo } from 'react';
import { useABTestingV2 } from '@/modules/abTesting';
import { AB_TESTS } from '@/modules/abTesting/constants';
import { useGetCommonStore } from '@/store';
import { ScContainer } from '@/styles/common';
import { GameCategories } from './Categories';
import { type IProvidersProps, Providers } from './Providers';
import { Search } from './Search';
import {
  ScFilterGames,
  ScFilterGamesContainer,
  ScFilterGamesMob,
  ScGamesSectionPoint,
  ScSearchGroup,
} from './styled';
import { Topics } from './Topics';

export type FilterGamesProps = IProvidersProps & IGameCategoriesProps;

export interface IGameCategoriesProps {
  categoryId?: string;
  subCategoryId?: string;
}

export const FilterGames: FC<FilterGamesProps> = memo(() => {
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const { provider, gameSubCategory, category } = useParams();

  const params = useMemo(
    () => ({
      providerId:
        typeof provider === 'string' ? decodeURIComponent(provider) : undefined,
      subCategoryId:
        typeof gameSubCategory === 'string'
          ? decodeURIComponent(gameSubCategory)
          : undefined,
      categoryId:
        typeof category === 'string' ? decodeURIComponent(category) : undefined,
    }),
    [category, provider, gameSubCategory]
  );

  return (
    <>
      <ScGamesSectionPoint id="gamesSection" />
      {isMobileDevice ? (
        <FilterGamesMobile {...params} />
      ) : (
        <FilterGamesDesktop {...params} />
      )}
    </>
  );
});

const FilterGamesDesktop: FC<FilterGamesProps> = ({
  providerId,
  categoryId,
  subCategoryId,
}) => (
  <ScFilterGamesContainer>
    <ScContainer>
      <ScFilterGames>
        <GameCategories subCategoryId={subCategoryId} />
        <ScSearchGroup>
          <Search />
          <Providers providerId={providerId} />
          <Topics categoryId={categoryId} />
        </ScSearchGroup>
      </ScFilterGames>
    </ScContainer>
  </ScFilterGamesContainer>
);

const FilterGamesMobile: FC<FilterGamesProps> = ({
  categoryId,
  providerId,
  ...props
}) => {
  const { assignABTestVersion, isB: isSearchHidden } = useABTestingV2({
    experiment: AB_TESTS.HIDE_SEARCH_ON_LOBBY,
  });

  useEffect(() => {
    assignABTestVersion();
  }, [assignABTestVersion]);

  return (
    <ScContainer>
      <ScFilterGamesMob smallMargin={isSearchHidden}>
        {!isSearchHidden && (
          <ScSearchGroup>
            <Search />
            <Providers providerId={providerId} />
            <Topics categoryId={categoryId} />
          </ScSearchGroup>
        )}
        <GameCategories {...props} />
      </ScFilterGamesMob>
    </ScContainer>
  );
};
